import { type FormEvent, useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock } from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { AuthLayout } from '@/components/layouts/AuthLayout';
import { useAuth } from '@/hooks/useAuth';
import { hasRole } from '@/types/auth';
import styles from './LoginPage.module.css';

interface LoginFormErrors {
  email: string;
  password: string;
}

const INITIAL_ERRORS: LoginFormErrors = { email: '', password: '' };

/**
 * Login page with email/password form.
 *
 * Features:
 * - Client-side validation before API call
 * - Loading state on submit button
 * - Error message display for invalid credentials
 * - Redirect to /admin/dashboard on success
 * - Focus management: auto-focus email field on mount
 * - Accessible form with proper labels and aria attributes
 */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error: authError, clearError } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<LoginFormErrors>(INITIAL_ERRORS);
  const [formError, setFormError] = useState<string | null>(null);

  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  /**
   * Clears API error when user modifies form fields.
   */
  function handleFieldChange(field: 'email' | 'password', value: string) {
    if (field === 'email') {
      setEmail(value);
    } else {
      setPassword(value);
    }

    if (formError) {
      setFormError(null);
    }
    if (authError) {
      clearError();
    }
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  }

  function validate(): boolean {
    const newErrors: LoginFormErrors = { email: '', password: '' };
    let isValid = true;

    if (!email.trim()) {
      newErrors.email = 'E-mail é obrigatório.';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Formato de e-mail inválido.';
      isValid = false;
    }

    if (!password) {
      newErrors.password = 'Senha é obrigatória.';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);

    if (!validate()) {
      return;
    }

    try {
      const loginResult = await login({ email: email.trim(), password });
      const from = (location.state as { from?: { pathname: string } })?.from?.pathname;
      // Redirect patients to portal, staff to admin
      const defaultPath = hasRole(loginResult, 'Patient')
        ? '/portal/dashboard'
        : '/admin/dashboard';
      navigate(from ?? defaultPath, { replace: true });
    } catch {
      setFormError(authError ?? 'Credenciais inválidas. Tente novamente.');
    }
  }

  const displayError = formError ?? authError;

  return (
    <AuthLayout>
      <div className={styles.card}>
        <header className={styles.header}>
          <h1 className={styles.title}>
            CRM<span className={styles.titleAccent}>Clinica</span>
          </h1>
          <p className={styles.subtitle}>Entre na sua conta</p>
        </header>

        {displayError && (
          <div className={styles.errorBanner} role="alert">
            <p>{displayError}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className={styles.form}>
          <Input
            ref={emailRef}
            label="Email"
            type="email"
            name="email"
            placeholder="seu@email.com"
            autoComplete="email"
            value={email}
            onChange={(e) => handleFieldChange('email', e.target.value)}
            error={errors.email}
            leftIcon={<Mail size={18} />}
            fullWidth
            required
          />

          <Input
            label="Senha"
            type="password"
            name="password"
            placeholder="Sua senha"
            autoComplete="current-password"
            value={password}
            onChange={(e) => handleFieldChange('password', e.target.value)}
            error={errors.password}
            leftIcon={<Lock size={18} />}
            fullWidth
            required
          />

          <div className={styles.forgotPasswordRow}>
            <Link to="/forgot-password" className={styles.forgotPasswordLink}>
              Esqueci minha senha
            </Link>
          </div>

          <Button type="submit" variant="primary" size="lg" fullWidth isLoading={isLoading}>
            Entrar
          </Button>
        </form>

        <p className={styles.registerLink}>
          Não tem conta?{' '}
          <Link to="/register">Cadastre-se</Link>
        </p>
      </div>
    </AuthLayout>
  );
}
